
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Profile } from '@/types/database.types';
import { UserRole } from '@/types/user-roles';
import { Loader2, Mail, Trash2, User } from 'lucide-react';

interface UserDetailDialogProps {
  user: Profile | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUserUpdated: () => void;
}

export const UserDetailDialog = ({ user, open, onOpenChange, onUserUpdated }: UserDetailDialogProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState<UserRole>(user?.user_type as UserRole || 'regular');
  const { toast } = useToast();

  if (!user) return null;

  const handleRoleChange = async () => {
    if (selectedRole === user.user_type) {
      toast({
        title: "No Changes",
        description: "The selected role is the same as the current role.",
      });
      return;
    }
    
    setIsLoading(true);
    try {
      console.log(`Updating user ${user.id} role from ${user.user_type} to ${selectedRole}`);
      
      const { error } = await supabase
        .from('profiles')
        .update({ 
          user_type: selectedRole,
          is_broker: selectedRole === 'broker'
        })
        .eq('id', user.id);

      if (error) {
        console.error('Role update error:', error);
        throw error;
      }

      console.log('Role updated successfully');

      toast({
        title: "User Role Updated",
        description: `${user.first_name} ${user.last_name}'s role has been changed to ${selectedRole}.`,
      });

      onUserUpdated();
    } catch (error: any) {
      console.error('Error updating user role:', error);
      toast({
        title: "Failed to Update Role",
        description: error.message || "An unexpected error occurred while updating the user role.",
        variant: "destructive",
      });
      
      // Reset the selected role to the original value
      setSelectedRole(user.user_type as UserRole || 'regular');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    setIsLoading(true);
    try {
      console.log('Attempting to send password reset for user:', user.email);
      
      const { data, error } = await supabase.auth.resetPasswordForEmail(user.email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        console.error('Password reset error:', error);
        throw error;
      }

      console.log('Password reset response:', data);

      toast({
        title: "Password Reset Sent",
        description: `A password reset email has been sent to ${user.email}.`,
      });
    } catch (error: any) {
      console.error('Error sending password reset:', error);
      toast({
        title: "Failed to Send Reset",
        description: error.message || "An error occurred while sending the password reset email.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    setIsLoading(true);
    try {
      console.log('Attempting to delete user via edge function:', user.id, user.email);

      const { data, error } = await supabase.functions.invoke('delete-user', {
        body: {
          userId: user.id
        }
      });

      console.log('Delete function response:', { data, error });

      if (error) {
        console.error('Error calling delete-user function:', error);
        throw new Error(`Failed to delete user: ${error.message}`);
      }

      if (!data || !data.success) {
        console.error('Delete function returned failure:', data);
        throw new Error(data?.error || 'Unknown error occurred during deletion');
      }

      console.log('Successfully deleted user:', user.email);

      toast({
        title: "User Deleted",
        description: `${user.first_name} ${user.last_name} has been successfully deleted from the system.`,
      });

      onOpenChange(false);
      onUserUpdated();
    } catch (error: any) {
      console.error('Error deleting user:', error);
      toast({
        title: "Failed to Delete User",
        description: error.message || "An unexpected error occurred while deleting the user.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {user.first_name} {user.last_name}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* User Info */}
          <div className="space-y-2">
            <div className="text-sm text-muted-foreground">Email</div>
            <div className="font-medium">{user.email}</div>
          </div>

          <div className="space-y-2">
            <div className="text-sm text-muted-foreground">User ID</div>
            <div className="font-mono text-xs text-muted-foreground">{user.id}</div>
          </div>

          <div className="space-y-2">
            <div className="text-sm text-muted-foreground">Member Since</div>
            <div className="font-medium">
              {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}
            </div>
          </div>

          {/* Role Management */}
          <div className="space-y-2">
            <div className="text-sm text-muted-foreground">User Role</div>
            <div className="flex items-center gap-2">
              <Select
                value={selectedRole}
                onValueChange={(value: UserRole) => setSelectedRole(value)}
                disabled={isLoading}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="regular">Regular User</SelectItem>
                  <SelectItem value="broker">Broker</SelectItem>
                  <SelectItem value="peo">PEO Provider</SelectItem>
                </SelectContent>
              </Select>
              {selectedRole !== user.user_type && (
                <Button 
                  onClick={handleRoleChange} 
                  disabled={isLoading}
                  size="sm"
                >
                  {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update'}
                </Button>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={handlePasswordReset}
              disabled={isLoading}
              className="flex items-center gap-2"
            >
              <Mail className="h-4 w-4" />
              Send Password Reset
            </Button>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="destructive"
                  disabled={isLoading}
                  className="flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete User
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete{' '}
                    <strong>{user.first_name} {user.last_name}</strong>'s account and remove all of their data from our servers.
                    <br /><br />
                    <strong>User ID:</strong> {user.id}
                    <br />
                    <strong>Email:</strong> {user.email}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeleteUser}
                    disabled={isLoading}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Deleting...
                      </>
                    ) : (
                      'Delete User'
                    )}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
